import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, X } from 'lucide-react';
import { SegmentedControl } from './SegmentedControl.js';

export type MultiSelectMode = 'include' | 'exclude';

export interface MultiSelectOption {
  value: string;
  label: string;
}

export function MultiSelect({
  options,
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  ariaLabel,
  mode = 'include',
  onModeChange,
}: {
  options: MultiSelectOption[];
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  ariaLabel?: string;
  mode?: MultiSelectMode;
  onModeChange?: (mode: MultiSelectMode) => void;
}) {
  const { t } = useTranslation();
  const emptyLabel = placeholder ?? t('common.all');
  const searchLabel = searchPlaceholder ?? t('common.search');
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [pos, setPos] = useState<{ top: number; left: number; width: number; maxHeight: number } | null>(
    null,
  );
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  function place() {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const width = Math.min(320, window.innerWidth - 16);
    let left = r.left;
    if (left + width > window.innerWidth - 8) left = Math.max(8, window.innerWidth - width - 8);
    const gap = 6;
    const spaceBelow = window.innerHeight - r.bottom - gap - 8;
    const spaceAbove = r.top - gap - 8;
    const openAbove = spaceBelow < 260 && spaceAbove > spaceBelow;
    const maxHeight = Math.max(180, Math.min(400, openAbove ? spaceAbove : spaceBelow));
    const top = openAbove ? Math.max(8, r.top - gap - maxHeight) : r.bottom + gap;
    setPos({ top, left, width, maxHeight });
  }

  useLayoutEffect(() => {
    if (!open) return;
    place();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      const n = e.target as Node;
      if (rootRef.current?.contains(n) || panelRef.current?.contains(n)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    function onMove() {
      place();
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [open]);

  const selected = useMemo(() => new Set(value), [value]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const triggerLabel =
    value.length === 0
      ? emptyLabel
      : `${mode === 'exclude' ? t('common.exclude') : t('common.include')} (${value.length})`;

  function toggle(id: string) {
    if (selected.has(id)) onChange(value.filter((v) => v !== id));
    else onChange([...value, id]);
  }

  const panel =
    open && pos
      ? createPortal(
          <div
            ref={panelRef}
            className="ui-multiselect-panel"
            role="listbox"
            aria-multiselectable="true"
            style={{ top: pos.top, left: pos.left, width: pos.width, maxHeight: pos.maxHeight }}
          >
            {onModeChange && (
              <SegmentedControl
                size="sm"
                ariaLabel={t('common.filterMode')}
                value={mode}
                onChange={onModeChange}
                options={[
                  { value: 'include', label: t('common.include') },
                  { value: 'exclude', label: t('common.exclude') },
                ]}
              />
            )}
            <div className="ui-multiselect-actions">
              <button type="button" onClick={() => onChange(options.map((o) => o.value))}>
                {t('common.selectAll')}
              </button>
              <button type="button" onClick={() => onChange([])}>
                {t('common.clear')}
              </button>
            </div>
            <input
              type="search"
              className="ui-multiselect-search"
              placeholder={searchLabel}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
            <div className="ui-multiselect-list">
              {filtered.length === 0 ? (
                <div className="ui-multiselect-empty">{t('common.noMatches')}</div>
              ) : (
                filtered.map((o) => {
                  const on = selected.has(o.value);
                  return (
                    <label
                      key={o.value}
                      className={on ? 'ui-multiselect-option is-on' : 'ui-multiselect-option'}
                    >
                      <input type="checkbox" checked={on} onChange={() => toggle(o.value)} />
                      <span>{o.label}</span>
                    </label>
                  );
                })
              )}
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <div className="ui-multiselect" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className="ui-multiselect-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((v) => !v)}
      >
        <span>{triggerLabel}</span>
        {value.length > 0 ? (
          <X
            onClick={(e) => {
              e.stopPropagation();
              onChange([]);
            }}
          />
        ) : (
          <ChevronDown />
        )}
      </button>
      {panel}
    </div>
  );
}
